import { parseFromValuesOrFunc } from 'blocks/cx-data-table/utils/utils'
import clsx from 'clsx'
import { ChevronDownIcon, LockIcon } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'primitives/dropdown-menu'
import { TableHead, type TableHeadProps } from 'primitives/table'
import { Tooltip } from 'primitives/tooltip'
import {
  type CSSProperties,
  type DragEventHandler,
  type MutableRefObject,
  type ReactNode,
  useMemo,
} from 'react'
import { useHover } from '../../hooks/use-hover'
import type {
  DataTableColumnVirtualizer,
  DataTableHeader,
  DataTableInstance,
  DataTableRowData,
} from '../../types'
import { parseCSSVarId } from '../../utils/style.utils'
import { TableRowCheckbox } from '../inputs/table-selection-checkbox'
import classes from './table-head-cell.module.css'
import { TableHeadCellGrabHandle } from './table-head-cell-grab-handle'
import { TableHeadCellResizeHandle } from './table-head-cell-resize-handle'
import { TableHeadCellSortLabel } from './table-head-sort-label'

interface Props<TData extends DataTableRowData> extends TableHeadProps {
  columnVirtualizer?: DataTableColumnVirtualizer
  header: DataTableHeader<TData>
  renderedHeaderIndex?: number
  table: DataTableInstance<TData>
}

export const DataTableHeadCell = <TData extends DataTableRowData>({
  columnVirtualizer,
  header,
  renderedHeaderIndex = 0,
  table,
  ...rest
}: Props<TData>) => {
  const {
    getState,
    options: {
      columnResizeDirection,
      columnResizeMode,
      enableColumnDragging,
      enableColumnOrdering,
      enableColumnPinning,
      enableGrouping,
      enableHeaderActionsHoverReveal,
      enableRowSelection,
      enableMultiSort,
      layoutMode,
      cxTableHeadCellProps,
      isSelectDropdown = true,
    },
    refs: { tableHeadCellRefs },
    setHoveredColumn,
  } = table
  const { columnSizingInfo, draggingColumn, grouping, hoveredColumn } = getState()
  const { column } = header
  const { columnDef } = column
  const { columnDefType } = columnDef

  const args = { column, table }

  const tableCellProps = {
    ...parseFromValuesOrFunc(cxTableHeadCellProps, args),
    ...rest,
  }

  const widthStyles: CSSProperties = {
    minWidth: `max(calc(var(--header-${parseCSSVarId(
      header?.id,
    )}-size) * 1px), ${columnDef.minSize ?? 30}px)`,
    width: `calc(var(--header-${parseCSSVarId(header.id)}-size) * 1px)`,
  }
  if (layoutMode === 'grid') {
    widthStyles.flex = `${
      [0, false].includes(columnDef.grow!) ? 0 : `var(--header-${parseCSSVarId(header.id)}-size)`
    } 0 auto`
  } else if (layoutMode === 'grid-no-grow') {
    widthStyles.flex = `${+(columnDef.grow || 0)} 0 auto`
  }

  const isColumnPinned =
    enableColumnPinning && columnDef.columnDefType !== 'group' && column.getIsPinned()

  const isDraggingColumn = draggingColumn?.id === column.id
  const isHoveredColumn = hoveredColumn?.id === column.id

  const { hovered: isHoveredHeadCell, ref: isHoveredHeadCellRef } = useHover<HTMLTableCellElement>()

  const showColumnButtons =
    !enableHeaderActionsHoverReveal ||
    (isHoveredHeadCell &&
      !table.getVisibleFlatColumns().find((columnVal) => columnVal.getIsResizing()))

  const showDragHandle =
    enableColumnDragging !== false &&
    columnDef.enableColumnDragging !== false &&
    (enableColumnDragging ||
      (enableColumnOrdering && columnDef.enableColumnOrdering !== false) ||
      (enableGrouping && columnDef.enableGrouping !== false && !grouping.includes(column.id))) &&
    !isColumnPinned

  const headerPL = useMemo(() => {
    let pl = 0
    if (column.getCanSort()) pl++
    if (showColumnButtons) pl += 1.75
    if (showDragHandle) pl += 1.25
    return pl
  }, [showColumnButtons, showDragHandle])

  const handleDragEnter: DragEventHandler<HTMLTableCellElement> = () => {
    if (enableGrouping && hoveredColumn?.id === 'drop-zone') {
      setHoveredColumn(null)
    }
    if (enableColumnOrdering && draggingColumn && columnDefType !== 'group') {
      setHoveredColumn(columnDef.enableColumnOrdering !== false ? column : null)
    }
  }

  const headerElement =
    columnDef?.Header instanceof Function
      ? columnDef?.Header?.({
          column,
          header,
          table,
        })
      : (columnDef?.Header ?? (columnDef.header as ReactNode))

  return (
    <TableHead
      colSpan={header.colSpan}
      data-column-pinned={isColumnPinned || undefined}
      data-dragging-column={isDraggingColumn || undefined}
      data-first-right-pinned={
        (isColumnPinned === 'right' && column.getIsFirstColumn(isColumnPinned)) || undefined
      }
      data-hovered-column-target={isHoveredColumn || undefined}
      data-index={renderedHeaderIndex}
      data-last-left-pinned={
        (isColumnPinned === 'left' && column.getIsLastColumn(isColumnPinned)) || undefined
      }
      data-resizing={
        (columnResizeMode === 'onChange' &&
          columnSizingInfo?.isResizingColumn === column.id &&
          columnResizeDirection) ||
        undefined
      }
      {...tableCellProps}
      style={{
        // @ts-expect-error passing css variables
        '--mrt-table-cell-left':
          isColumnPinned === 'left' ? `${column.getStart(isColumnPinned)}` : undefined,
        '--mrt-table-cell-right':
          isColumnPinned === 'right' ? `${column.getAfter(isColumnPinned)}` : undefined,
        ...widthStyles,
      }}
      align={columnDefType === 'group' ? 'center' : 'left'}
      className={clsx(
        classes.root,
        layoutMode?.startsWith('grid') && classes['root-grid'],
        enableMultiSort && column.getCanSort() && 'select-none',
        columnVirtualizer && 'transition-none',
        tableCellProps?.className,
      )}
      onDragEnter={handleDragEnter}
      ref={(node: HTMLTableCellElement) => {
        if (node) {
          tableHeadCellRefs.current[column.id] = node
          ;(isHoveredHeadCellRef as MutableRefObject<HTMLTableCellElement>).current = node
          if (columnDefType !== 'group') {
            columnVirtualizer?.measureElement?.(node)
          }
        }
      }}
    >
      {header.isPlaceholder
        ? null
        : (tableCellProps.children ?? (
            <div
              className={clsx(
                'mrt-table-head-cell-content',
                classes.content,
                (columnDefType === 'group' || tableCellProps?.align === 'center') &&
                  'justify-center',
                tableCellProps?.align === 'right' && 'flex-row-reverse',
                column.getCanResize() && 'justify-between',
              )}
            >
              <div
                style={
                  {
                    '--mrt-table-head-cell-labels-padding-left': `${headerPL}rem`,
                  } as React.CSSProperties
                }
                className={clsx(
                  'mrt-table-head-cell-labels',
                  classes.labels,
                  column.getCanSort() && columnDefType !== 'group' && 'cursor-pointer',
                  tableCellProps?.align === 'right'
                    ? classes['labels-right']
                    : tableCellProps?.align === 'center' && classes['labels-center'],
                  columnDefType === 'data' && 'overflow-hidden',
                )}
                onClick={column.getToggleSortingHandler()}
              >
                {column.getIsFirstColumn() && enableRowSelection ? (
                  <div className="flex items-center gap-1">
                    <TableRowCheckbox
                      {...{
                        checked: table.getIsAllRowsSelected(),
                        indeterminate: table.getIsSomeRowsSelected(),
                        onChange: table.getToggleAllRowsSelectedHandler(),
                      }}
                    />
                    {isSelectDropdown && (
                      <DropdownMenu>
                        <Tooltip content="Selection options">
                          <DropdownMenuTrigger
                            className="h-4 w-4 hover:bg-accent hover:text-accent-foreground rounded-sm"
                            data-testid="button-selection-options"
                          >
                            <ChevronDownIcon className="h-4 w-4" />
                          </DropdownMenuTrigger>
                        </Tooltip>
                        <DropdownMenuContent>
                          <DropdownMenuItem
                            data-testid="button-select-all"
                            onClick={() => {
                              table.toggleAllRowsSelected(true)
                              table.options.onSelectAllRecords?.(false)
                            }}
                          >
                            Select this page
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              table.toggleAllRowsSelected(true)
                              table.options.onSelectAllRecords?.(true)
                            }}
                            data-testid="button-deselect-all"
                          >
                            Select all
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                ) : null}
                {isColumnPinned && (
                  <LockIcon
                    aria-hidden
                    className="size-3.5 shrink-0 text-tertiary"
                    strokeWidth={1.5}
                  />
                )}

                <div
                  className={clsx(
                    'mrt-table-head-cell-content-wrapper',
                    classes['content-wrapper'],
                    columnDefType === 'data' && 'overflow-hidden',
                    (columnDef.header?.length ?? 0) < 20 && 'whitespace-nowrap',
                  )}
                >
                  {headerElement}
                </div>
                {column.getCanSort() && (column.getIsSorted() || showColumnButtons) && (
                  <TableHeadCellSortLabel header={header} table={table} />
                )}
              </div>
              {columnDefType !== 'group' && (
                <div
                  className={clsx(
                    'mrt-table-head-cell-content-actions',
                    'flex items-center self-center whitespace-nowrap gap-0.5 ml-0.5',
                  )}
                >
                  {showDragHandle && (
                    <TableHeadCellGrabHandle
                      column={column}
                      table={table}
                      tableHeadCellRef={{
                        current: tableHeadCellRefs.current[column.id],
                      }}
                    />
                  )}
                </div>
              )}
              {column.getCanResize() && <TableHeadCellResizeHandle header={header} table={table} />}
            </div>
          ))}
    </TableHead>
  )
}
